import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useCanvasStore } from './canvas-store'
import { screenToWorld, zoomAroundPoint, type Point, type Rect } from './geometry'
import { DotGrid } from './dot-grid'

type Props = {
  children: ReactNode
  // Surfaced for parent overlays (paste hint, empty-state, etc.) that need to know whether
  // any items exist. The canvas itself doesn't read items — items are passed as children.
  isEmpty?: boolean
  // Drop dispatch. Receives the world-space drop point + the native DataTransfer payload.
  // Canvas owns the rect-to-world math so callers don't have to think about transforms.
  onDrop?: (worldPosition: Point, data: DataTransfer) => void
  // Marquee dispatch. Fired on pointer-up after a drag on the empty canvas. The world
  // rect describes the rectangle the user dragged; caller decides which items it captures.
  // `toggle` is true when the user held shift — caller should flip each boxed item's
  // membership in the existing selection (add unselected, remove selected) rather than
  // replace it.
  onMarquee?: (worldRect: Rect, toggle: boolean) => void
  // Bounding rects (world coords) for items currently being dragged — feeds the
  // dot-grid drag halo. Edge-anchored so big items get a visible halo around them.
  dragHalos?: Rect[]
}

// Modifier key (Space) — when held, drag on empty canvas pans instead of marqueeing.
// Tracked outside React so wheel/pointer handlers can read it without rerenders.
function useSpaceHeld() {
  const ref = useRef(false)
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !isEditableTarget(e.target)) {
        ref.current = true
      }
    }
    const up = (e: KeyboardEvent) => {
      if (e.code === 'Space') ref.current = false
    }
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', up)
    return () => {
      window.removeEventListener('keydown', down)
      window.removeEventListener('keyup', up)
    }
  }, [])
  return ref
}

function isEditableTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null
  if (!el || !el.tagName) return false
  if ((el as HTMLElement).isContentEditable) return true
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT'
}

// Infinite canvas. The viewport is `overflow-hidden`; the inner `.world` div takes the
// CSS transform (translate + scale) so its absolutely-positioned children read as if
// they live in world space.
//
// Pointer-down on empty canvas:
//   - Space held → pan
//   - Otherwise  → marquee select

export function Canvas({ children, isEmpty, onDrop, onMarquee, dragHalos }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [cursorScreen, setCursorScreen] = useState<Point | null>(null)
  const spaceHeld = useSpaceHeld()

  const isPanningRef = useRef(false)
  const panOriginRef = useRef<{
    pointerId: number
    touch: boolean
    screen: { x: number; y: number }
    offset: { x: number; y: number }
  } | null>(null)

  // Marquee state lives in React because we re-render the rectangle every move.
  const [marquee, setMarquee] = useState<{
    start: Point
    current: Point
    toggle: boolean
  } | null>(null)

  const scale = useCanvasStore((s) => s.scale)
  const offset = useCanvasStore((s) => s.offset)
  const setTransform = useCanvasStore((s) => s.setTransform)
  const setViewport = useCanvasStore((s) => s.setViewport)
  const setSelected = useCanvasStore((s) => s.setSelected)

  // Track viewport size so paste / wheel-zoom / marquee math has a frame of reference.
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const cr = entry.contentRect
      setViewport({ width: cr.width, height: cr.height })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [setViewport])

  // Wheel: pinch / ctrl-wheel = zoom around cursor; plain wheel = two-axis pan.
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const rect = el.getBoundingClientRect()
      const screenPoint = { x: e.clientX - rect.left, y: e.clientY - rect.top }
      const { scale: currScale, offset: currOffset } = useCanvasStore.getState()
      if (e.ctrlKey || e.metaKey) {
        const next = currScale * Math.pow(2, -e.deltaY * 0.005)
        const t = zoomAroundPoint({ scale: currScale, offset: currOffset }, screenPoint, next)
        setTransform({ scale: t.scale, offset: t.offset })
      } else {
        setTransform({
          offset: { x: currOffset.x - e.deltaX, y: currOffset.y - e.deltaY },
        })
      }
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [setTransform])

  const screenPointFromEvent = (e: React.PointerEvent<HTMLDivElement>): Point | null => {
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return null
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }

  const onPointerDownBackground = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    // A second finger while one is already panning: ignore it so the pan doesn't jump.
    if (isPanningRef.current) return
    const sp = screenPointFromEvent(e)
    if (!sp) return
    // Touch has no space key, and a one-finger marquee leaves phone users unable to
    // move around the board, so a finger drag on empty canvas always pans.
    const touch = e.pointerType === 'touch'
    if (spaceHeld.current || touch) {
      // Pan.
      isPanningRef.current = true
      panOriginRef.current = {
        pointerId: e.pointerId,
        touch,
        screen: { x: e.clientX, y: e.clientY },
        offset: { ...offset },
      }
    } else {
      // Marquee. Selection isn't cleared yet — wait for pointer-up so a no-op click on
      // empty space (drag < threshold) doesn't deselect the user's existing selection
      // unless they explicitly clicked without shift.
      setMarquee({ start: sp, current: sp, toggle: e.shiftKey })
    }
    ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
  }

  const onPointerMoveBackground = (e: React.PointerEvent<HTMLDivElement>) => {
    // Update cursor position regardless of pan/marquee state — the dot grid uses it
    // for the magnetism effect.
    const sp = screenPointFromEvent(e)
    if (sp) setCursorScreen(sp)

    if (isPanningRef.current && panOriginRef.current) {
      if (e.pointerId !== panOriginRef.current.pointerId) return
      const dx = e.clientX - panOriginRef.current.screen.x
      const dy = e.clientY - panOriginRef.current.screen.y
      setTransform({
        offset: {
          x: panOriginRef.current.offset.x + dx,
          y: panOriginRef.current.offset.y + dy,
        },
      })
      return
    }
    if (marquee) {
      const sp = screenPointFromEvent(e)
      if (sp) setMarquee({ ...marquee, current: sp })
    }
  }

  const onPointerUpBackground = (e: React.PointerEvent<HTMLDivElement>) => {
    ;(e.target as HTMLElement).releasePointerCapture?.(e.pointerId)
    const pan = panOriginRef.current
    if (pan && e.pointerId !== pan.pointerId) return
    isPanningRef.current = false
    panOriginRef.current = null
    // A tap on empty canvas still deselects on touch, where every drag is a pan.
    if (pan?.touch) {
      const moved = Math.hypot(e.clientX - pan.screen.x, e.clientY - pan.screen.y) > 4
      if (!moved) setSelected(new Set())
    }

    if (!marquee) return
    const start = marquee.start
    const end = marquee.current
    const toggle = marquee.toggle
    setMarquee(null)
    const moved = Math.hypot(end.x - start.x, end.y - start.y) > 4
    if (!moved) {
      // A click on empty space — clear selection unless shift was held.
      if (!toggle) setSelected(new Set())
      return
    }
    if (onMarquee) {
      const worldStart = screenToWorld(start, { scale, offset })
      const worldEnd = screenToWorld(end, { scale, offset })
      const worldRect: Rect = {
        x: Math.min(worldStart.x, worldEnd.x),
        y: Math.min(worldStart.y, worldEnd.y),
        w: Math.abs(worldEnd.x - worldStart.x),
        h: Math.abs(worldEnd.y - worldStart.y),
      }
      onMarquee(worldRect, toggle)
    }
  }

  const viewport = useCanvasStore((s) => s.viewport)
  // Convert the cursor's screen position to world coords for the DotGrid.
  const cursorWorld = cursorScreen ? screenToWorld(cursorScreen, { scale, offset }) : null

  const cursorClass = spaceHeld.current ? 'cursor-grab active:cursor-grabbing' : 'cursor-crosshair'

  return (
    <div
      ref={containerRef}
      className={`bg-background relative h-full w-full touch-none select-none overflow-hidden ${cursorClass}`}
      onPointerDown={onPointerDownBackground}
      onPointerMove={onPointerMoveBackground}
      onPointerUp={onPointerUpBackground}
      onPointerCancel={onPointerUpBackground}
      onPointerLeave={() => setCursorScreen(null)}
      onDragOver={(e) => {
        if (!onDrop) return
        e.preventDefault()
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
        setIsDragOver(true)
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        if (!onDrop) return
        e.preventDefault()
        setIsDragOver(false)
        const rect = containerRef.current?.getBoundingClientRect()
        if (!rect) return
        const world = screenToWorld(
          { x: e.clientX - rect.left, y: e.clientY - rect.top },
          { scale, offset },
        )
        onDrop(world, e.dataTransfer)
      }}>
      <DotGrid
        scale={scale}
        offset={offset}
        viewport={viewport}
        cursorWorld={cursorWorld}
        dragHalos={dragHalos ?? []}
      />
      <div
        className="absolute left-0 top-0 origin-top-left"
        style={{
          transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
        }}>
        {children}
      </div>
      {marquee && (
        <div
          className="border-primary/60 bg-primary/10 pointer-events-none absolute border"
          style={{
            left: Math.min(marquee.start.x, marquee.current.x),
            top: Math.min(marquee.start.y, marquee.current.y),
            width: Math.abs(marquee.current.x - marquee.start.x),
            height: Math.abs(marquee.current.y - marquee.start.y),
          }}
        />
      )}
      {isEmpty && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="text-muted-foreground text-center">
            <p className="text-base font-medium">Drop anything here</p>
            <p className="mt-1 text-sm">Paste text, links, images, PDFs, videos, fonts.</p>
          </div>
        </div>
      )}
      {isDragOver && (
        <div className="border-accent/60 bg-accent/5 pointer-events-none absolute inset-2 rounded-xl border-2 border-dashed" />
      )}
    </div>
  )
}
